import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { tryParseMoney } from '@sitzmark/core';
import { Badge, Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import { sanitiseAmount } from './amount.js';

/**
 * A card at the register through BlueSage (docs/PAYMENTS.md, decisions
 * 229–231).
 *
 * The server owns every payment attempt (§8 D); this screen only shows what
 * the server knows, asking every 1.5 seconds while one waits. It never decides
 * a payment failed: waiting is said as waiting, unknown as unknown.
 */

/** Whether the register takes cards through BlueSage, and in which mode. */
export function useRegisterCards(): { enabled: boolean; sandbox: boolean } {
  const mode = useQuery({
    queryKey: ['payments', 'registerMode'],
    queryFn: () => api.payments.registerMode.query(),
    staleTime: 30_000,
  });
  return { enabled: mode.data?.enabled === true, sandbox: mode.data?.mode === 'sandbox' };
}

type Attempt = NonNullable<Awaited<ReturnType<typeof api.payments.cardAttempt.query>>>;

const HOLDING = new Set(['sending', 'waiting', 'unknown']);
export const holdsSale = (attempt: Attempt | null | undefined) =>
  attempt != null && HOLDING.has(attempt.status);

/** The card payment on a sale, asked for every 1.5 seconds while one waits. */
export function useCardAttempt(saleId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['payments', 'cardAttempt', saleId],
    queryFn: () => api.payments.cardAttempt.query({ saleId }),
    enabled,
    staleTime: 0,
    refetchInterval: (query) =>
      holdsSale(query.state.data as Attempt | null | undefined) ? 1_500 : false,
  });
}

function seconds(since: Date | string, now: number): string {
  const total = Math.max(0, Math.round((now - new Date(since).getTime()) / 1000));
  return total < 60
    ? `${total}s`
    : `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}

export function TerminalCardPanel({
  saleId,
  registerId,
  dueCents,
  isRefund,
  sandbox,
  autoSend = false,
  onAutoSent = () => undefined,
  onSaleChanged,
  onCompleted,
}: {
  saleId: string;
  registerId: string;
  /** What is owed on a card right now (negative on a refund). */
  dueCents: number | null;
  isRefund: boolean;
  sandbox: boolean;
  /**
   * *Pay card* on the bar was just pressed: send the balance without asking —
   * once. The panel says when it has (`onAutoSent`), so the press is used up
   * and never carries over to the next sale (found by the browser suite: a
   * remembered press sent the next sale's balance the moment Card was chosen).
   */
  autoSend?: boolean;
  onAutoSent?: () => void;
  onSaleChanged: () => void;
  onCompleted: (number: string) => void;
}) {
  const queryClient = useQueryClient();
  const attempt = useCardAttempt(saleId, true);
  const current = attempt.data ?? null;
  const holding = holdsSale(current);
  const [choosing, setChoosing] = useState(false);
  const [typed, setTyped] = useState('');
  const [keyed, setKeyed] = useState<number | null>(null);
  const [approving, setApproving] = useState<null | {
    kind: 'release' | 'refund' | 'manual';
    amountCents: number;
    terminalId: string | null;
  }>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!holding) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [holding]);

  const picker = useQuery({
    queryKey: ['payments', 'terminalPicker', registerId],
    queryFn: () => api.payments.terminalPicker.query({ registerId }),
    staleTime: 0,
    // Fresh while the chooser is on screen — opened by Change, or because the
    // default was busy or resting — so a terminal coming free is seen.
    refetchInterval: (query) => (choosing || query.state.data?.automatic == null ? 3_000 : false),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['payments', 'cardAttempt', saleId] });
    void queryClient.invalidateQueries({ queryKey: ['payments', 'terminalPicker', registerId] });
    onSaleChanged();
  };

  const send = useMutation({
    mutationFn: (input: {
      amountCents: number;
      terminalId: string | null;
      override?: ManagerOverride;
    }) =>
      api.payments.sendToTerminal.mutate({
        saleId,
        registerId,
        amountCents: input.amountCents,
        terminalId: input.terminalId,
        kind: isRefund ? 'terminal_refund' : 'terminal_sale',
        ...(input.override === undefined ? {} : { override: input.override }),
      }),
    onSuccess: (result) => {
      setApproving(null);
      if (result.outcome === 'choose') {
        setChoosing(true);
        return;
      }
      setChoosing(false);
      setTyped('');
      queryClient.setQueryData(['payments', 'cardAttempt', saleId], result.attempt);
      refresh();
    },
    onError: (error, input) => {
      if (isOverridable(error))
        setApproving({
          kind: 'refund',
          amountCents: input.amountCents,
          terminalId: input.terminalId,
        });
    },
  });

  const cancel = useMutation({
    mutationFn: (attemptId: string) => api.payments.cancelCard.mutate({ attemptId }),
    onSuccess: refresh,
  });
  const check = useMutation({
    mutationFn: (attemptId: string) => api.payments.checkCard.mutate({ attemptId }),
    onSuccess: refresh,
  });
  const release = useMutation({
    mutationFn: (input: { attemptId: string; override?: ManagerOverride }) =>
      api.payments.markNotCharged.mutate({
        attemptId: input.attemptId,
        ...(input.override === undefined ? {} : { override: input.override }),
      }),
    onSuccess: () => {
      setApproving(null);
      refresh();
    },
    onError: (error) => {
      if (isOverridable(error)) setApproving({ kind: 'release', amountCents: 0, terminalId: null });
    },
  });
  const makeDefault = useMutation({
    mutationFn: (terminalId: string) =>
      api.payments.setDefaultTerminal.mutate({ terminalId, registerId }),
    onSuccess: () => void picker.refetch(),
  });

  // When the payment completes the sale, the register moves on.
  const completedNumber =
    current?.status === 'approved' && current.saleStatus === 'completed'
      ? current.saleNumber
      : null;
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (completedNumber === null || current === null || reported.current === current.id) return;
    reported.current = current.id;
    onCompleted(completedNumber);
  }, [completedNumber, current, onCompleted]);

  // Pay card on the bar: send the whole balance to this register's default, at once.
  const whole = dueCents === null ? null : Math.abs(dueCents);
  useEffect(() => {
    if (!autoSend) return;
    if (attempt.isLoading || whole === null) return;
    // Used up whether or not it sends: a payment already waiting, or nothing
    // owed, means there is nothing to send, now or later.
    onAutoSent();
    if (whole <= 0 || holding || isRefund) return;
    send.mutate({ amountCents: whole, terminalId: null });
    // The press is the trigger, not the values it reads.
  }, [autoSend, attempt.isLoading, whole, holding, isRefund]);

  const automatic = picker.data?.automatic ?? null;
  const typedCents = typed === '' ? null : tryParseMoney(typed);
  const typedReason =
    typed === ''
      ? undefined
      : typedCents == null
        ? 'That is not an amount'
        : typedCents <= 0
          ? 'Enter more than zero'
          : whole !== null && typedCents > whole
            ? `That is more than is owed — the most is ${money(whole)}`
            : undefined;
  const settling = whole === null ? 'Working out what is owed…' : undefined;

  // --- a payment on the terminal ------------------------------------------------
  if (current !== null && holding) {
    const unknown = current.status === 'unknown';
    return (
      <div className="card-terminal" aria-live="polite">
        <div className={`card-terminal__status${unknown ? ' card-terminal__status--unknown' : ''}`}>
          {unknown ? null : <span className="card-terminal__pulse" aria-hidden="true" />}
          <div>
            <p className="card-terminal__message">{current.message}</p>
            <p className="text-secondary num">Started {seconds(current.startedAt, now)} ago</p>
          </div>
        </div>
        {unknown ? (
          <div className="card-terminal__actions">
            <Button
              variant="primary"
              busy={check.isPending}
              onClick={() => check.mutate(current.id)}
            >
              Check again
            </Button>
            <Button
              variant="danger"
              busy={release.isPending}
              onClick={() => release.mutate({ attemptId: current.id })}
            >
              Not charged — I checked the terminal
            </Button>
          </div>
        ) : (
          <div className="card-terminal__actions">
            <Button
              variant="secondary"
              busy={cancel.isPending}
              disabledReason={
                current.cancelRequested ? 'Asked — waiting for the terminal to answer' : undefined
              }
              onClick={() => cancel.mutate(current.id)}
            >
              Cancel on the terminal
            </Button>
          </div>
        )}
        {release.isError && !isOverridable(release.error) ? (
          <Callout tone="danger">{errorMessage(release.error)}</Callout>
        ) : null}
        {approving?.kind === 'release' ? (
          <ManagerApproval
            title="Manager approval needed"
            because="Marking a card payment not charged frees the sale. Check the terminal shows it declined, or nothing, first."
            busy={release.isPending}
            error={release.isError ? approvalRefusal(release.error) : null}
            onCancel={() => setApproving(null)}
            onApprove={(override) => release.mutate({ attemptId: current.id, override })}
          />
        ) : null}
      </div>
    );
  }

  // --- choosing a terminal --------------------------------------------------------
  const sendTo = (terminalId: string | null, amountCents: number) =>
    send.mutate({ amountCents, terminalId });
  const amountNow = typedCents != null && typedReason === undefined ? typedCents : (whole ?? 0);

  return (
    <div className="card-terminal">
      {sandbox ? (
        <Callout tone="info">
          Sandbox: card payments here go to BlueSage’s test system and never move money.
        </Callout>
      ) : null}
      {current?.status === 'not_paid' ? <Callout tone="warning">{current.message}</Callout> : null}
      {current?.status === 'approved' && current.saleStatus !== 'completed' ? (
        <Callout tone={current.attention === null ? 'success' : 'warning'}>
          {current.attention ?? current.message}
        </Callout>
      ) : null}
      {current?.status === 'released' ? <Callout tone="info">{current.message}</Callout> : null}

      {choosing || automatic === null ? (
        <div className="card-terminal__picker">
          <p className="field__label">Which terminal?</p>
          {picker.isLoading ? <p className="text-secondary">Reading the terminals…</p> : null}
          {picker.data !== undefined && picker.data.choices.length === 0 ? (
            <Callout tone="warning">
              No terminals are set up for this store yet. An admin adds them in Settings → Card
              payments.
            </Callout>
          ) : null}
          <ul className="card-terminal__list">
            {(picker.data?.choices ?? []).map((choice) => (
              <li key={choice.terminal.id} className="card-terminal__row">
                <div>
                  <span className="card-terminal__name">{choice.terminal.name}</span>
                  <span className="text-secondary"> · {choice.state}</span>
                  {choice.defaultOf !== null ? (
                    <Badge
                      tone={choice.defaultOf === 'This register’s default' ? 'success' : 'neutral'}
                    >
                      {choice.defaultOf}
                    </Badge>
                  ) : null}
                </div>
                <div className="card-terminal__row-actions">
                  {choice.defaultOf === 'This register’s default' ? null : (
                    <Button
                      variant="ghost"
                      busy={makeDefault.isPending}
                      onClick={() => makeDefault.mutate(choice.terminal.id)}
                    >
                      Make this register’s default
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    busy={send.isPending}
                    disabledReason={settling ?? (choice.free ? undefined : choice.state)}
                    onClick={() => sendTo(choice.terminal.id, amountNow)}
                  >
                    {isRefund ? `Refund ${money(amountNow)} here` : `Send ${money(amountNow)} here`}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="card-terminal__ready">
          <p className="text-secondary">
            Terminal: <strong>{automatic.name}</strong>{' '}
            <button type="button" className="link-button" onClick={() => setChoosing(true)}>
              Change
            </button>
          </p>
          <Button
            variant="primary"
            size="large"
            busy={send.isPending}
            disabledReason={settling}
            onClick={() => sendTo(null, whole ?? 0)}
          >
            {isRefund
              ? `Refund ${money(whole ?? 0)} on ${automatic.name}`
              : `Send ${money(whole ?? 0)} to ${automatic.name}`}
          </Button>
        </div>
      )}

      {isRefund ? null : (
        <div className="tender__custom">
          <label className="field__label" htmlFor="card-part">
            Part of it on this card
          </label>
          <input
            id="card-part"
            className="input money"
            inputMode="decimal"
            placeholder="0.00"
            value={typed}
            onChange={(event) => setTyped(sanitiseAmount(event.target.value))}
          />
          <Button
            variant="secondary"
            busy={send.isPending}
            disabledReason={
              settling ?? (typedCents == null ? 'Type the amount for this card' : typedReason)
            }
            onClick={() => {
              if (typedCents != null) sendTo(automatic?.id ?? null, typedCents);
            }}
          >
            {typedCents == null ? 'Send part' : `Send ${money(typedCents)}`}
          </Button>
        </div>
      )}

      {isRefund ? null : (
        <div className="card-terminal__other">
          <Button
            variant="ghost"
            disabledReason={
              settling ?? (whole !== null && whole <= 0 ? 'Nothing is owed' : undefined)
            }
            onClick={() =>
              setKeyed(typedCents != null && typedReason === undefined ? typedCents : whole)
            }
          >
            Key in a card (phone order)
          </Button>
        </div>
      )}

      {send.isError && !isOverridable(send.error) ? (
        <Callout tone="danger">{errorMessage(send.error)}</Callout>
      ) : null}
      {approving?.kind === 'refund' ? (
        <ManagerApproval
          title="Manager approval needed"
          because="Refunding to a card at the terminal gives money back."
          busy={send.isPending}
          error={send.isError ? approvalRefusal(send.error) : null}
          onCancel={() => setApproving(null)}
          onApprove={(override) =>
            send.mutate({
              amountCents: approving.amountCents,
              terminalId: approving.terminalId,
              override,
            })
          }
        />
      ) : null}
      {keyed !== null ? (
        <KeyedCardDialog
          saleId={saleId}
          registerId={registerId}
          amountCents={keyed}
          onClose={() => setKeyed(null)}
          onDone={() => {
            setKeyed(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Keying a card for a phone order (flow 4): BlueSage's own card fields in a
 * frame. The card never touches this page — only a one-time token, accepted
 * from BlueSage's origin and nowhere else (criterion 20).
 */
export function KeyedCardDialog({
  saleId,
  registerId,
  amountCents,
  onClose,
  onDone,
}: {
  saleId: string;
  registerId: string;
  amountCents: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [height, setHeight] = useState(260);
  const [outcome, setOutcome] = useState<Attempt | null>(null);
  const form = useQuery({
    queryKey: ['payments', 'keyedForm', saleId, amountCents],
    queryFn: () =>
      api.payments.keyedForm.mutate({ saleId, amountCents, origin: window.location.origin }),
    staleTime: Infinity,
    retry: false,
  });
  const charge = useMutation({
    mutationFn: (cardToken: string) =>
      api.payments.keyedCharge.mutate({ saleId, registerId, amountCents, cardToken }),
    onSuccess: (attempt) => {
      setOutcome(attempt);
      if (attempt.status === 'approved') onDone();
    },
  });

  const origin = form.data?.embedOrigin ?? null;
  useEffect(() => {
    if (origin === null) return undefined;
    const listen = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      const data = event.data as { type?: unknown; card_token?: unknown; height?: unknown } | null;
      if (
        data?.type === 'bluesage:card-token' &&
        typeof data.card_token === 'string' &&
        !charge.isPending
      ) {
        charge.mutate(data.card_token);
      }
      if (data?.type === 'bluesage:resize' && typeof data.height === 'number') {
        setHeight(Math.min(Math.max(data.height, 160), 600));
      }
    };
    window.addEventListener('message', listen);
    return () => window.removeEventListener('message', listen);
  }, [origin, charge]);

  return (
    <Modal
      open
      title={`Key in a card · ${money(amountCents)}`}
      description="Type the card the customer reads to you. It goes straight to BlueSage; Shekko never sees the number."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {form.isError ? <Callout tone="danger">{errorMessage(form.error)}</Callout> : null}
      {form.data !== undefined && outcome?.status !== 'approved' ? (
        <iframe
          className="card-terminal__frame"
          title="Card number, expiry and security code"
          src={form.data.embedUrl}
          style={{ height }}
        />
      ) : null}
      {charge.isPending ? <p className="text-secondary">Charging the card…</p> : null}
      {outcome !== null && outcome.status !== 'approved' ? (
        <Callout tone="warning">{outcome.message}</Callout>
      ) : null}
      {charge.isError ? <Callout tone="danger">{errorMessage(charge.error)}</Callout> : null}
    </Modal>
  );
}

/**
 * Refunding a return to the card it was paid with (decision 231): one row per
 * card payment on the original sale, each up to what is left on it. No card
 * needed. **The amount is the server's** (decision 250): the card's share of
 * what is owed, as that card was actually charged.
 */
export function ReturnToCardPanel({
  saleId,
  registerId,
  dueCents,
  onSaleChanged,
  onCompleted,
}: {
  saleId: string;
  registerId: string;
  dueCents: number | null;
  onSaleChanged: () => void;
  onCompleted: (number: string) => void;
}) {
  const queryClient = useQueryClient();
  const cards = useQuery({
    // Keyed on what is owed: each card's share follows the return's lines.
    queryKey: ['payments', 'refundableCards', saleId, dueCents],
    queryFn: () => api.payments.refundableCards.query({ saleId }),
  });
  const [approving, setApproving] = useState<{ tenderId: string } | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const refund = useMutation({
    mutationFn: (input: { tenderId: string; override?: ManagerOverride }) =>
      api.payments.refundReturnToCard.mutate({
        saleId,
        originalTenderId: input.tenderId,
        registerId,
        ...(input.override === undefined ? {} : { override: input.override }),
      }),
    onSuccess: (result) => {
      setApproving(null);
      void queryClient.invalidateQueries({ queryKey: ['payments', 'refundableCards', saleId] });
      onSaleChanged();
      if (result.result.outcome !== 'refunded') {
        setRefused(result.result.words);
        return;
      }
      setRefused(null);
      if (result.sale.sale.status === 'completed' && result.sale.sale.number !== null)
        onCompleted(result.sale.sale.number);
    },
    onError: (error, input) => {
      if (isOverridable(error)) setApproving({ tenderId: input.tenderId });
    },
  });

  const usable = (cards.data ?? []).filter((card) => card.refundableCents > 0);
  if (cards.isLoading || usable.length === 0) return null;
  return (
    <div className="card-terminal__refunds">
      <p className="field__label">Back to the card it was paid with</p>
      {usable.map((card) => {
        const amount = card.refundCents ?? 0;
        return (
          <Button
            key={card.tenderId}
            variant="primary"
            busy={refund.isPending}
            disabledReason={
              dueCents === null
                ? 'Working out what is owed…'
                : amount <= 0
                  ? 'Nothing is owed back'
                  : undefined
            }
            onClick={() => refund.mutate({ tenderId: card.tenderId })}
          >
            Refund {money(amount)} to {card.cardBrand ?? 'card'}{' '}
            {card.cardLast4 === null ? '' : `•${card.cardLast4}`}
          </Button>
        );
      })}
      {refused !== null ? <Callout tone="warning">{refused}</Callout> : null}
      {refund.isError && !isOverridable(refund.error) ? (
        <Callout tone="danger">{errorMessage(refund.error)}</Callout>
      ) : null}
      {approving !== null ? (
        <ManagerApproval
          title="Manager approval needed"
          because="Refunding to a card gives money back."
          busy={refund.isPending}
          error={refund.isError ? approvalRefusal(refund.error) : null}
          onCancel={() => setApproving(null)}
          onApprove={(override) => refund.mutate({ ...approving, override })}
        />
      ) : null}
    </div>
  );
}
